import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';

const fmtINR = (v: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(v);

export function RoiCalculator() {
  return (
    <div>
      <ToolHeader
        toolId="roi-calculator"
        title="ROI calculator"
        description="Lump sum and SIP returns with CAGR. Visualize wealth over time."
      />
      <Tabs defaultValue="lump">
        <TabsList className="mb-4">
          <TabsTrigger value="lump">Lump sum</TabsTrigger>
          <TabsTrigger value="sip">SIP</TabsTrigger>
          <TabsTrigger value="simple">Simple ROI</TabsTrigger>
        </TabsList>
        <TabsContent value="lump"><LumpSumPanel /></TabsContent>
        <TabsContent value="sip"><SipPanel /></TabsContent>
        <TabsContent value="simple"><SimplePanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function LumpSumPanel() {
  const [principal, setPrincipal] = useState(500_000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const result = useMemo(() => {
    const future = principal * Math.pow(1 + rate / 100, years);
    const gain = future - principal;
    return { future, gain, multiple: future / principal };
  }, [principal, rate, years]);

  const sparkData = useMemo(() => {
    const points: { year: number; value: number }[] = [];
    for (let y = 0; y <= years; y++) {
      points.push({ year: y, value: principal * Math.pow(1 + rate / 100, y) });
    }
    return points;
  }, [principal, rate, years]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="space-y-4">
        <Field label="Principal (₹)" hint={fmtINR(principal)}>
          <Input
            type="number"
            value={principal}
            onChange={(e) => setPrincipal(Math.max(0, Number(e.target.value) || 0))}
            className="font-mono"
          />
          <Slider value={principal} onChange={setPrincipal} min={10_000} max={10_000_000} step={10_000} showValue={false} className="mt-3" />
        </Field>
        <Field label="Expected return" hint={`${rate.toFixed(1)}% p.a.`}>
          <Slider value={rate} onChange={setRate} min={1} max={30} step={0.5} showValue={false} />
        </Field>
        <Field label="Tenure" hint={`${years} years`}>
          <Slider value={years} onChange={setYears} min={1} max={40} showValue={false} />
        </Field>
      </Card>

      <Card>
        <div className="text-2xs uppercase tracking-wider text-fg-subtle">Future value</div>
        <div className="text-4xl font-semibold mt-2 font-mono tabular-nums">{fmtINR(result.future)}</div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Stat label="Wealth gained" value={fmtINR(result.gain)} highlight />
          <Stat label="Multiple" value={`${result.multiple.toFixed(2)}×`} />
        </div>
        <div className="mt-6">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-2">Growth curve</div>
          <Sparkline data={sparkData} principal={principal} />
        </div>
      </Card>
    </div>
  );
}

function SipPanel() {
  const [monthly, setMonthly] = useState(20_000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const result = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    const future = monthly * (((1 + r) ** n - 1) / r) * (1 + r);
    const invested = monthly * n;
    const gain = future - invested;
    return { future, invested, gain };
  }, [monthly, rate, years]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="space-y-4">
        <Field label="Monthly SIP (₹)" hint={fmtINR(monthly)}>
          <Input
            type="number"
            value={monthly}
            onChange={(e) => setMonthly(Math.max(0, Number(e.target.value) || 0))}
            className="font-mono"
          />
          <Slider value={monthly} onChange={setMonthly} min={500} max={200_000} step={500} showValue={false} className="mt-3" />
        </Field>
        <Field label="Expected return" hint={`${rate.toFixed(1)}% p.a.`}>
          <Slider value={rate} onChange={setRate} min={1} max={30} step={0.5} showValue={false} />
        </Field>
        <Field label="Tenure" hint={`${years} years`}>
          <Slider value={years} onChange={setYears} min={1} max={40} showValue={false} />
        </Field>
      </Card>

      <Card>
        <div className="text-2xs uppercase tracking-wider text-fg-subtle">Future value</div>
        <div className="text-4xl font-semibold mt-2 font-mono tabular-nums">{fmtINR(result.future)}</div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Stat label="Invested" value={fmtINR(result.invested)} />
          <Stat label="Gain" value={fmtINR(result.gain)} highlight />
        </div>
        <div className="mt-6">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-2">Composition</div>
          <div className="flex h-3 rounded-full overflow-hidden bg-bg-inset">
            <div className="bg-accent" style={{ width: `${(result.invested / result.future) * 100}%` }} />
            <div className="bg-warning" style={{ width: `${(result.gain / result.future) * 100}%` }} />
          </div>
          <div className="flex items-center justify-between text-2xs text-fg-subtle mt-2">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-accent" />
              Invested
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-warning" />
              Returns
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}

function SimplePanel() {
  const [initial, setInitial] = useState(100000);
  const [final, setFinal] = useState(150000);
  const [years, setYears] = useState(2);

  const result = useMemo(() => {
    const gain = final - initial;
    const total = initial > 0 ? (gain / initial) * 100 : 0;
    const cagr = initial > 0 && years > 0 ? (Math.pow(final / initial, 1 / years) - 1) * 100 : 0;
    return { gain, total, cagr };
  }, [initial, final, years]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="space-y-3">
        <Field label="Initial investment (₹)">
          <Input type="number" value={initial} onChange={(e) => setInitial(Number(e.target.value) || 0)} className="font-mono" />
        </Field>
        <Field label="Final value (₹)">
          <Input type="number" value={final} onChange={(e) => setFinal(Number(e.target.value) || 0)} className="font-mono" />
        </Field>
        <Field label="Years held">
          <Input type="number" value={years} step={0.25} onChange={(e) => setYears(Number(e.target.value) || 0)} className="font-mono" />
        </Field>
      </Card>
      <Card>
        <Stat label="Total return" value={`${result.total.toFixed(2)}%`} big />
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Stat label="CAGR" value={`${result.cagr.toFixed(2)}%`} highlight />
          <Stat label="Gain" value={fmtINR(result.gain)} />
        </div>
      </Card>
    </div>
  );
}

function Sparkline({ data, principal }: { data: { year: number; value: number }[]; principal: number }) {
  if (data.length === 0) return null;
  const w = 280;
  const h = 80;
  const max = Math.max(...data.map((d) => d.value));
  const min = principal;
  const path = data
    .map((d, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((d.value - min) / (max - min)) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
      <defs>
        <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.4" />
          <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L ${w} ${h} L 0 ${h} Z`} fill="url(#grad)" />
      <path d={path} fill="none" stroke="hsl(var(--accent))" strokeWidth="2" />
    </svg>
  );
}

function Stat({
  label,
  value,
  highlight,
  big,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  big?: boolean;
}) {
  return (
    <div className={big ? '' : `p-3 rounded-lg border ${highlight ? 'bg-warning/10 border-warning/20 text-warning' : 'bg-bg-inset border-border'}`}>
      <div className="text-2xs uppercase tracking-wider opacity-70">{label}</div>
      <div className={`font-mono font-semibold mt-1 tabular-nums ${big ? 'text-4xl' : 'text-sm'}`}>
        {value}
      </div>
    </div>
  );
}

export default RoiCalculator;
